import React, { useEffect, useRef, useState } from "react";
import './MyProfile-sidebar.scss';
import { useNavigate } from "react-router-dom";
import { useRequiredProfile } from "../hooks/useProfileData";
import { STORAGE_KEYS, clearAuthStorage } from "../utils/storage";
import { ThemeAppearanceSelect } from "./ThemeAppearanceSelect";
import { useToast } from "../Contexts/ToastContext";
import { PageLoadLoader } from "../Components/Loader";
import axios from "axios";
import { LinkedAccountsModal } from "./LinkedAccountsModal";
import { NotificationSettingsSection } from "./NotificationSettingsSection";

export function MyProfile() {
    const navigate = useNavigate();
    const isGuest = typeof window !== 'undefined' && localStorage.getItem('guestUser') === 'true';
    const [activeTab, setActiveTab] = useState(isGuest ? 'settings' : 'profile');
    const [isSticky, setIsSticky] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const firstNameInputRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
    const [showLinkedAccountsModal, setShowLinkedAccountsModal] = useState(false);
    const [changePasswordForm, setChangePasswordForm] = useState({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    const [changePasswordLoading, setChangePasswordLoading] = useState(false);
    const [changePasswordMessage, setChangePasswordMessage] = useState('');
    const [deleteAccountConfirmText, setDeleteAccountConfirmText] = useState('');
    const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
    const [deleteAccountMessage, setDeleteAccountMessage] = useState('');
    const { profileData, loading, error, shouldShowProfile, fetchProfile, clearProfile } = useRequiredProfile();
    const { showToast } = useToast();

    console.log('profileData', profileData);
    
    // Form data state
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        pincode: ""
    });

    // Initialize form data when profile loads
    useEffect(() => {
        if (profileData?.data) {
            const userData = profileData.data;
            setFormData({
                firstName: userData.firstName || "",
                lastName: userData.lastName || "",
                phone: userData.phone || "",
                email: userData.email || "",
                pincode: userData.pincode || ""
            });
        }
    }, [profileData]);

    useEffect(() => {
        if (isEditing && firstNameInputRef.current) {
            firstNameInputRef.current.focus();
        }
    }, [isEditing]);

    useEffect(() => {
        const handleScroll = () => {
            const breadcrumbsHeight = 6.2 * 16 + 80;
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            
            if (scrollTop >= breadcrumbsHeight) {
                setIsSticky(true);
            } else {
                setIsSticky(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditToggle = () => {
        if (isEditing) {
            // Reset form data to original values
            if (profileData?.data) {
                const userData = profileData.data;
                setFormData({
                    firstName: userData.firstName || "",
                    lastName: userData.lastName || "",
                    phone: userData.phone || "",
                    email: userData.email || "",
                    pincode: userData.pincode || ""
                });
            }
        }
        setIsEditing(!isEditing);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            const config = {
                headers: { Authorization: `Bearer ${token}` }
            };

            // You'll need to update this with your actual update endpoint
            const response = await axios.put('/users/profile', formData, config);
            
            if (response.status === 200) {
                // Refresh profile data
                await fetchProfile();
                setIsEditing(false);
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            showToast({ message: 'Failed to update profile. Please try again.' });
        } finally {
            setIsSaving(false);
        }
    };

    const openChangePasswordModal = () => setShowChangePasswordModal(true);
    const closeChangePasswordModal = () => {
        setShowChangePasswordModal(false);
        setChangePasswordForm({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
        setChangePasswordMessage('');
    };

    const handleChangePasswordSubmit = async (e) => {
        e.preventDefault();
        const { currentPassword, newPassword, confirmNewPassword } = changePasswordForm;
        if (!currentPassword.trim()) {
            setChangePasswordMessage('Current password is required.');
            return;
        }
        if (!newPassword.trim()) {
            setChangePasswordMessage('New password is required.');
            return;
        }
        if (newPassword.length < 6) {
            setChangePasswordMessage('New password must be at least 6 characters.');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            setChangePasswordMessage('New password and confirmation do not match.');
            return;
        }
        setChangePasswordLoading(true);
        setChangePasswordMessage('');
        try {
            const token = localStorage.getItem('token');
            await axios.patch('/users/change-password', {
                currentPassword,
                newPassword
            }, { headers: { Authorization: `Bearer ${token}` } });
            closeChangePasswordModal();
            clearAuthStorage();
            navigate('/auth/login');
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.msg || 'Failed to change password. Please try again.';
            setChangePasswordMessage(msg);
        } finally {
            setChangePasswordLoading(false);
        }
    };

    const openDeleteAccountModal = () => {
        setShowDeleteAccountModal(true);
        setDeleteAccountConfirmText('');
        setDeleteAccountMessage('');
    };
    const closeDeleteAccountModal = () => {
        setShowDeleteAccountModal(false);
        setDeleteAccountConfirmText('');
        setDeleteAccountMessage('');
    };

    const handleDeleteAccountConfirm = async () => {
        if (deleteAccountConfirmText.trim().toUpperCase() !== 'DELETE') {
            setDeleteAccountMessage('Please type DELETE to confirm.');
            return;
        }
        setDeleteAccountLoading(true);
        setDeleteAccountMessage('');
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setDeleteAccountMessage('You are not logged in.');
                return;
            }
            await axios.delete('/users/account', {
                headers: { Authorization: `Bearer ${token}` },
            });
            closeDeleteAccountModal();
            clearProfile();
            clearAuthStorage();
            showToast({ message: 'Your account has been deleted.' });
            navigate('/auth/login', { replace: true });
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.msg || 'Failed to delete account. Please try again.';
            setDeleteAccountMessage(msg);
        } finally {
            setDeleteAccountLoading(false);
        }
    };

    // Show loading state (skip for guest - no API fetch)
    if (!isGuest && loading) {
        return <PageLoadLoader />;
    }

    // Show error state
    if (error) {
        return (
            <div className="web-my-profile-container sidebar">
                <div className="profile-page">
                    <div className="error-container">
                        <div className="error-message">Error loading profile. Please try again.</div>
                        <button onClick={() => window.location.reload()}>Retry</button>
                    </div>
                </div>
            </div>
        );
    }

    // Show profile data - for guest, use localStorage
    const userData = profileData?.data || {};
    const firstName = isGuest ? (localStorage.getItem('guestFirstName') || '') : (userData.firstName || '');
    const lastName = isGuest ? (localStorage.getItem('guestLastName') || '') : (userData.lastName || '');
    const { phone, email, pincode, isVerified, ordersCount = 0, cartCount = 0, reviewsCount = 0 } = userData;
    const passwordSet = userData.passwordSet === true || (typeof window !== 'undefined' && localStorage.getItem('passwordSet') === 'true');
    const guestOrderCount = isGuest ? parseInt(localStorage.getItem(STORAGE_KEYS.guestOrderCount) || '0', 10) : 0;
    const guestCartCount = isGuest
        ? (JSON.parse(localStorage.getItem(STORAGE_KEYS.guestCart) || '[]').reduce((s, i) => s + (i.selectedQty || 1), 0))
        : 0;

    return (
        <div className="web-my-profile-container sidebar">
            <div className="profile-page">
                <div className="breadcrumbs-container">
                    <a href="/" className="breadcrumbs-link">Home</a>
                    <span className="breadcrumbs-separator">/</span>
                    <a href="/profile" className="breadcrumbs-bold-link breadcrumbs-link">My Profile</a>
                </div>

                <div className={`profile-sidebar ${isSticky ? "profile-sidebar-sticky" : ""}`}>
                    <div className="sidebar-content">
                        <div className="profile-avatar-section">
                            <div className="profile-avatar">
                                <i className="bi bi-person-fill"></i>
                            </div>
                            <h3 className="profile-name">{firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'User'}</h3>
                            <p className="profile-role">{isGuest ? 'Guest' : 'Premium Member'}</p>
                            <div className="sidebar-stats">
                                <button type="button" className="sidebar-stat" onClick={() => navigate('/orders')}>
                                    <span className="sidebar-stat-number">{isGuest ? guestOrderCount : ordersCount}</span>
                                    <span className="sidebar-stat-label">Orders</span>
                                </button>
                                <button type="button" className="sidebar-stat" onClick={() => navigate('/cart')}>
                                    <span className="sidebar-stat-number">{isGuest ? guestCartCount : cartCount}</span>
                                    <span className="sidebar-stat-label">Cart</span>
                                </button>
                                {!isGuest && (
                                    <div className="sidebar-stat sidebar-stat--static">
                                        <span className="sidebar-stat-number">{reviewsCount}</span>
                                        <span className="sidebar-stat-label">Reviews</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="sidebar-menu">
                            {!isGuest && (
                                <>
                                    <button
                                        type="button"
                                        className={`menu-item ${activeTab === 'profile' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('profile')}
                                    >
                                        <i className="bi bi-person menu-icon"></i>
                                        Profile Details
                                    </button>
                                    <button
                                        type="button"
                                        className={`menu-item ${activeTab === 'settings' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('settings')}
                                    >
                                        <i className="bi bi-gear menu-icon"></i>
                                        Settings
                                    </button>
                                    <button
                                        type="button"
                                        className="menu-item menu-item--logout"
                                        onClick={() => {
                                            clearAuthStorage();
                                            navigate('/auth/login');
                                        }}
                                    >
                                        <i className="bi bi-box-arrow-right menu-icon"></i>
                                        Logout
                                    </button>
                                </>
                            )}
                            {isGuest && (
                                <button
                                    type="button"
                                    className={`menu-item ${activeTab === 'settings' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('settings')}
                                >
                                    <i className="bi bi-gear menu-icon"></i>
                                    Settings
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="main-content">
                    {activeTab === 'profile' && (
                    <div className="profile-card">
                        <div className="profile-header">
                            <h2 className="section-title">Profile Details</h2>
                        </div>
                        <div className="profile-content">
                            {!isEditing ? (
                                // View Mode
                                <>
                                    <div className="profile-info-grid">
                                        <div className="info-item">
                                            <span className="info-label">First Name</span>
                                            <span className="info-value">{firstName || '- not added -'}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Last Name</span>
                                            <span className="info-value">{lastName || '- not added -'}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Mobile Number</span>
                                            <span className="info-value">{phone || '- not added -'}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Email ID</span>
                                            <span className="info-value">{email || '- not added -'}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Pincode</span>
                                            <span className="info-value">{pincode || '- not added -'}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Verification Status</span>
                                            <span className="info-value">{isVerified ? 'Verified' : 'Not Verified'}</span>
                                        </div>
                                    </div>
                                    <div className="profile-actions">
                                        <button className="profile-edit-button" onClick={handleEditToggle}>
                                            <i className="bi bi-pencil-square edit-icon"></i>
                                            Edit Profile
                                        </button>
                                    </div>
                                </>
                            ) : (
                                // Edit Mode
                                <>
                                    <div className="profile-edit-form">
                                        <div className="form-group">
                                            <label className="form-label">First Name</label>
                                            <input
                                                ref={firstNameInputRef}
                                                type="text"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                placeholder="Enter first name"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Last Name</label>
                                            <input
                                                type="text"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                placeholder="Enter last name"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Mobile Number</label>
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                placeholder="Enter mobile number"
                                                maxLength="10"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Email ID</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                placeholder="Enter email"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Pincode</label>
                                            <input
                                                type="text"
                                                name="pincode"
                                                value={formData.pincode}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                placeholder="Enter pincode"
                                                maxLength="6"
                                            />
                                        </div>
                                    </div>
                                    <div className="profile-actions">
                                        <button 
                                            className="profile-save-button" 
                                            onClick={handleSave}
                                            disabled={isSaving}
                                        >
                                            <i className="bi bi-check-circle save-icon"></i>
                                            {isSaving ? 'Saving...' : 'Save Changes'}
                                        </button>
                                        <button 
                                            className="profile-cancel-button" 
                                            onClick={handleEditToggle}
                                            disabled={isSaving}
                                        >
                                            <i className="bi bi-x-circle cancel-icon"></i>
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    )}
                    {activeTab === 'settings' && (
                    <div className="profile-card settings-card">
                        <div className="profile-header">
                            <h2 className="section-title">Settings</h2>
                        </div>
                        <div className="profile-content">
                            {isGuest && (
                                <div className="settings-guest-notice">
                                    <i className="bi bi-info-circle"></i>
                                    <p>As a guest, your <strong>Orders</strong> and <strong>Wishlist</strong> are stored on this device (this browser). They work without an account and are saved in localStorage.</p>
                                </div>
                            )}
                            <div className="settings-section">
                                <h4 className="settings-section-title">General</h4>
                                <div className="settings-row">
                                    <span><i className="bi bi-globe"></i> Language</span>
                                    <span className="settings-value">English</span>
                                </div>
                                <ThemeAppearanceSelect />
                            </div>

                            <NotificationSettingsSection
                                profileData={profileData}
                                isGuest={isGuest}
                                onSaved={() => fetchProfile(true)}
                            />

                            {!isGuest && (
                            <>
                            <div className="settings-section">
                                <h4 className="settings-section-title">Account</h4>
                                {passwordSet && (
                                    <div className="settings-row clickable" onClick={openChangePasswordModal}>
                                        <span><i className="bi bi-key"></i> Change password</span>
                                        <i className="bi bi-chevron-right settings-row-chevron"></i>
                                    </div>
                                )}
                                {passwordSet && (
                                    <div className="settings-row">
                                        <span><i className="bi bi-calendar-check"></i> Last password change</span>
                                        <span className="settings-value">—</span>
                                    </div>
                                )}
                                <div className="settings-row clickable" onClick={openDeleteAccountModal}>
                                    <span><i className="bi bi-trash"></i> Delete account</span>
                                    <i className="bi bi-chevron-right settings-row-chevron"></i>
                                </div>
                                <div className="settings-row clickable" onClick={() => setShowLinkedAccountsModal(true)}>
                                    <span><i className="bi bi-link-45deg"></i> Linked accounts (Google, Facebook, Apple)</span>
                                    <i className="bi bi-chevron-right settings-row-chevron"></i>
                                </div>
                            </div>

                            <div className="settings-section">
                                <h4 className="settings-section-title">Security alerts</h4>
                                <div className="settings-row">
                                    <span><i className="bi bi-bell"></i> Alerts (suspicious login, password change)</span>
                                    <span className="settings-value">Email</span>
                                </div>
                            </div>
                            </>
                            )}

                            <div className="settings-section">
                                <h4 className="settings-section-title">Legal &amp; About</h4>
                                <div className="settings-row clickable" onClick={() => navigate('/terms')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/terms')}>
                                    <span><i className="bi bi-file-text"></i> Terms of Service</span>
                                    <i className="bi bi-chevron-right settings-row-chevron"></i>
                                </div>
                                <div className="settings-row clickable" onClick={() => navigate('/privacy')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/privacy')}>
                                    <span><i className="bi bi-shield-check"></i> Privacy Policy</span>
                                    <i className="bi bi-chevron-right settings-row-chevron"></i>
                                </div>
                                <div className="settings-row">
                                    <span><i className="bi bi-info-circle"></i> App version</span>
                                    <span className="settings-value">1.0.0</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}

                    <LinkedAccountsModal
                        isOpen={showLinkedAccountsModal}
                        onClose={() => setShowLinkedAccountsModal(false)}
                        loginType={userData?.loginType}
                    />

                    {/* Change password modal */}
                    {showChangePasswordModal && (
                        <div className="profile-modal-overlay" onClick={() => !changePasswordLoading && closeChangePasswordModal()}>
                            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="profile-modal-header">
                                    <h3>Change password</h3>
                                    <button type="button" className="profile-modal-close" onClick={closeChangePasswordModal} disabled={changePasswordLoading} aria-label="Close">×</button>
                                </div>
                                <form onSubmit={handleChangePasswordSubmit} className="profile-modal-body">
                                    <div className="profile-modal-field">
                                        <label htmlFor="profile-current-password">Current password</label>
                                        <input
                                            id="profile-current-password"
                                            type="password"
                                            value={changePasswordForm.currentPassword}
                                            onChange={(e) => setChangePasswordForm((p) => ({ ...p, currentPassword: e.target.value }))}
                                            placeholder="Enter current password"
                                            autoComplete="current-password"
                                        />
                                    </div>
                                    <div className="profile-modal-field">
                                        <label htmlFor="profile-new-password">New password</label>
                                        <input
                                            id="profile-new-password"
                                            type="password"
                                            value={changePasswordForm.newPassword}
                                            onChange={(e) => setChangePasswordForm((p) => ({ ...p, newPassword: e.target.value }))}
                                            placeholder="At least 6 characters"
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    <div className="profile-modal-field">
                                        <label htmlFor="profile-confirm-password">Confirm new password</label>
                                        <input
                                            id="profile-confirm-password"
                                            type="password"
                                            value={changePasswordForm.confirmNewPassword}
                                            onChange={(e) => setChangePasswordForm((p) => ({ ...p, confirmNewPassword: e.target.value }))}
                                            placeholder="Confirm new password"
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    {changePasswordMessage && <div className="profile-modal-message profile-modal-message--error">{changePasswordMessage}</div>}
                                    <div className="profile-modal-footer">
                                        <button type="button" className="profile-modal-btn-secondary" onClick={closeChangePasswordModal} disabled={changePasswordLoading}>Cancel</button>
                                        <button type="submit" className="profile-modal-btn-primary" disabled={changePasswordLoading}>{changePasswordLoading ? 'Updating...' : 'Change password'}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Delete account modal */}
                    {showDeleteAccountModal && (
                        <div className="profile-modal-overlay" onClick={() => !deleteAccountLoading && closeDeleteAccountModal()}>
                            <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="profile-modal-header">
                                    <h3>Delete account</h3>
                                    <button type="button" className="profile-modal-close" onClick={closeDeleteAccountModal} disabled={deleteAccountLoading} aria-label="Close">×</button>
                                </div>
                                <div className="profile-modal-body">
                                    <p className="profile-modal-warning">This action cannot be undone. All your data will be permanently removed. Type <strong>DELETE</strong> below to confirm.</p>
                                    <div className="profile-modal-field">
                                        <input
                                            type="text"
                                            value={deleteAccountConfirmText}
                                            onChange={(e) => setDeleteAccountConfirmText(e.target.value)}
                                            placeholder="Type DELETE to confirm"
                                            className={deleteAccountMessage ? 'profile-modal-input-error' : ''}
                                        />
                                    </div>
                                    {deleteAccountMessage && <div className="profile-modal-message profile-modal-message--error">{deleteAccountMessage}</div>}
                                    <div className="profile-modal-footer">
                                        <button type="button" className="profile-modal-btn-secondary" onClick={closeDeleteAccountModal} disabled={deleteAccountLoading}>Cancel</button>
                                        <button type="button" className="profile-modal-btn-danger" onClick={handleDeleteAccountConfirm} disabled={deleteAccountLoading || deleteAccountConfirmText.trim().toUpperCase() !== 'DELETE'}>
                                            {deleteAccountLoading ? 'Deleting...' : 'Delete my account'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
